import { ApiClient, ApiError, type EnvFileEntry, type EnvFileSnapshot } from '../lib/api.js';
import { el } from '../lib/dom.js';

export interface EnvFileViewDeps {
  api: ApiClient;
  onDeployRequested?: () => void;
}

interface KvBinding {
  entry: { type: 'kv'; key: string; value: string; masked?: boolean };
  rowEl: HTMLElement;
  keyInput: HTMLInputElement;
  valueInput: HTMLInputElement;
  toggleBtn: HTMLButtonElement;
  revealed: boolean;
}

const ENV_KEY_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function renderEnvFile(deps: EnvFileViewDeps): HTMLElement {
  // Single source of truth: the ordered entries array. UI rows only exist
  // for kv entries; comments and blanks are preserved invisibly so they
  // round-trip back to disk untouched.
  let entries: EnvFileEntry[] = [];
  let bindings: KvBinding[] = [];
  let snapshotExists = false;
  let allRevealed = false;
  let dirty = false;

  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
  const pathLabel = el('code', { class: 'env-file-path', text: '…' });
  const rowsContainer = el('div', { class: 'env-rows' });
  const emptyHint = el('p', {
    class: 'muted env-empty',
    text: 'No variables yet — add one to get started.',
    attrs: { hidden: true },
  });

  const saveBtn = el('button', { class: 'primary', text: 'Save', attrs: { disabled: true } });
  const saveDeployBtn = el('button', {
    class: 'primary',
    text: 'Save & deploy',
    attrs: { disabled: true, title: 'Save the .env, then jump to the Deploy tab' },
  });
  const reloadBtn = el('button', { class: 'ghost', text: 'Reload' });
  const addBtn = el('button', { text: '+ Add variable' });
  const revealAllBtn = el('button', { class: 'ghost', text: 'Show all values' });

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
  };
  const clearAlert = () => alertBox.setAttribute('hidden', '');

  const setDirty = (next: boolean) => {
    dirty = next;
    if (next) {
      saveBtn.removeAttribute('disabled');
      saveDeployBtn.removeAttribute('disabled');
    } else {
      saveBtn.setAttribute('disabled', '');
      saveDeployBtn.setAttribute('disabled', '');
    }
  };

  const updateEmptyHint = () => {
    if (bindings.length === 0) emptyHint.removeAttribute('hidden');
    else emptyHint.setAttribute('hidden', '');
  };

  const setRowRevealed = (b: KvBinding, revealed: boolean) => {
    b.revealed = revealed;
    b.valueInput.type = revealed ? 'text' : 'password';
    b.toggleBtn.textContent = revealed ? 'Hide' : 'Show';
    b.toggleBtn.setAttribute('aria-label', revealed ? 'Hide value' : 'Show value');
  };

  const makeBinding = (entry: { type: 'kv'; key: string; value: string; masked?: boolean }): KvBinding => {
    const startsMasked = entry.masked === true;
    const keyInput = el('input', {
      class: 'env-key mono',
      attrs: {
        type: 'text',
        value: entry.key,
        placeholder: 'KEY',
        spellcheck: false,
        autocapitalize: 'off',
        autocorrect: 'off',
        autocomplete: 'off',
      },
    }) as HTMLInputElement;
    const valueInput = el('input', {
      class: 'env-value mono',
      attrs: {
        // Masked (secret) values are never sent to the client: show the
        // server's "••••last4" hint as placeholder with an empty field, so
        // typing replaces and an untouched field round-trips as "keep".
        type: startsMasked ? 'text' : (allRevealed ? 'text' : 'password'),
        value: startsMasked ? '' : entry.value,
        placeholder: startsMasked ? `${entry.value} (stored — type to replace, Show to reveal)` : 'value',
        spellcheck: false,
        autocapitalize: 'off',
        autocorrect: 'off',
        autocomplete: 'off',
      },
    }) as HTMLInputElement;

    const toggleBtn = el('button', {
      class: 'ghost env-toggle',
      text: allRevealed ? 'Hide' : 'Show',
      attrs: { type: 'button', 'aria-label': allRevealed ? 'Hide value' : 'Show value' },
    }) as HTMLButtonElement;
    const deleteBtn = el('button', {
      class: 'ghost env-delete',
      text: 'Delete',
      attrs: { type: 'button', 'aria-label': 'Delete variable' },
    });

    const rowEl = el('div', {
      class: 'env-row',
      children: [keyInput, valueInput, toggleBtn, deleteBtn],
    });

    const binding: KvBinding = {
      entry,
      rowEl,
      keyInput,
      valueInput,
      toggleBtn,
      revealed: allRevealed,
    };

    keyInput.addEventListener('input', () => {
      binding.entry.key = keyInput.value;
      keyInput.classList.toggle(
        'invalid',
        keyInput.value !== '' && !ENV_KEY_REGEX.test(keyInput.value),
      );
      setDirty(true);
    });
    valueInput.addEventListener('input', () => {
      // Typing into a masked field replaces the secret — it's no longer "keep
      // stored", so drop the masked flag and use the typed value.
      binding.entry.masked = false;
      binding.entry.value = valueInput.value;
      setDirty(true);
    });
    toggleBtn.addEventListener('click', () => {
      if (binding.entry.masked) {
        // Fetch the real value on demand, then treat the field as cleartext.
        toggleBtn.setAttribute('disabled', '');
        deps.api
          .revealEnvValue(binding.entry.key)
          .then(({ value }) => {
            binding.entry.masked = false;
            binding.entry.value = value;
            binding.valueInput.value = value;
            binding.valueInput.placeholder = 'value';
            setRowRevealed(binding, true);
          })
          .catch((err) => {
            const msg = err instanceof ApiError ? err.message : 'Failed to reveal value';
            showAlert(msg);
          })
          .finally(() => toggleBtn.removeAttribute('disabled'));
        return;
      }
      setRowRevealed(binding, !binding.revealed);
    });
    deleteBtn.addEventListener('click', () => {
      const i = entries.indexOf(binding.entry);
      if (i !== -1) entries.splice(i, 1);
      bindings = bindings.filter((b) => b !== binding);
      rowEl.remove();
      updateEmptyHint();
      setDirty(true);
    });

    return binding;
  };

  const renderFromSnapshot = (snap: EnvFileSnapshot) => {
    snapshotExists = snap.exists;
    pathLabel.textContent = snap.path;
    entries = snap.entries.map((e) => ({ ...e }));
    bindings = [];
    while (rowsContainer.firstChild) rowsContainer.removeChild(rowsContainer.firstChild);

    for (const entry of entries) {
      if (entry.type !== 'kv') continue;
      const binding = makeBinding(entry);
      bindings.push(binding);
      rowsContainer.append(binding.rowEl);
    }

    updateEmptyHint();
    setDirty(false);
    if (!snap.exists) {
      showAlert(`No .env file at ${snap.path} yet — saving will create it.`, 'success');
    } else {
      clearAlert();
    }
  };

  const reload = async () => {
    if (dirty) {
      const ok = window.confirm('Discard unsaved changes and reload from disk?');
      if (!ok) return;
    }
    clearAlert();
    reloadBtn.setAttribute('disabled', '');
    try {
      const snap = await deps.api.getEnvFile();
      renderFromSnapshot(snap);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to load .env';
      showAlert(msg);
    } finally {
      reloadBtn.removeAttribute('disabled');
    }
  };

  const buildPayload = (): EnvFileEntry[] =>
    entries.map((e) => {
      if (e.type === 'kv') {
        // A still-masked entry was never revealed/edited: round-trip the flag
        // (value ignored server-side) so the stored secret is preserved.
        if (e.masked) return { type: 'kv', key: e.key.trim(), value: '', masked: true };
        return { type: 'kv', key: e.key.trim(), value: e.value };
      }
      if (e.type === 'comment') return { type: 'comment', text: e.text };
      return { type: 'blank' };
    });

  const validate = (): string | null => {
    const seen = new Set<string>();
    for (const b of bindings) {
      const key = b.entry.key.trim();
      if (key === '') return 'Empty key — fill it in or delete the row.';
      if (!ENV_KEY_REGEX.test(key)) {
        return `Invalid key "${key}" — use letters, digits, underscores; must not start with a digit.`;
      }
      if (seen.has(key)) return `Duplicate key: ${key}`;
      seen.add(key);
      if (/[\r\n]/.test(b.entry.value)) return `Value for ${key} contains a newline (not supported).`;
    }
    return null;
  };

  const save = async (): Promise<boolean> => {
    clearAlert();
    const err = validate();
    if (err) {
      showAlert(err);
      return false;
    }
    saveBtn.setAttribute('disabled', '');
    saveDeployBtn.setAttribute('disabled', '');
    try {
      const snap = await deps.api.saveEnvFile(buildPayload());
      renderFromSnapshot(snap);
      showAlert(snapshotExists ? '.env saved.' : '.env created.', 'success');
      return true;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Failed to save .env';
      showAlert(msg);
      setDirty(true);
      return false;
    }
  };

  saveBtn.addEventListener('click', () => {
    void save();
  });
  saveDeployBtn.addEventListener('click', async () => {
    const ok = await save();
    if (ok) deps.onDeployRequested?.();
  });
  reloadBtn.addEventListener('click', () => {
    void reload();
  });
  addBtn.addEventListener('click', () => {
    const entry: { type: 'kv'; key: string; value: string } = { type: 'kv', key: '', value: '' };
    entries.push(entry);
    const binding = makeBinding(entry);
    bindings.push(binding);
    rowsContainer.append(binding.rowEl);
    updateEmptyHint();
    setDirty(true);
    binding.keyInput.focus();
  });
  revealAllBtn.addEventListener('click', () => {
    allRevealed = !allRevealed;
    revealAllBtn.textContent = allRevealed ? 'Mask all values' : 'Show all values';
    for (const b of bindings) setRowRevealed(b, allRevealed);
  });

  const beforeUnload = (ev: BeforeUnloadEvent) => {
    if (dirty) {
      ev.preventDefault();
      ev.returnValue = '';
    }
  };
  window.addEventListener('beforeunload', beforeUnload);

  const view = el('div', {
    children: [
      el('div', {
        class: 'card',
        children: [
          el('h2', { text: 'Environment' }),
          el('p', {
            class: 'muted',
            children: [
              'Edits ',
              pathLabel,
              ' on the host. Values are masked by default — click ',
              el('strong', { text: 'Show' }),
              ' to reveal. A ',
              el('code', { text: '.env.bak' }),
              ' is written next to the file on every save so you can roll back.',
            ],
          }),
          alertBox,
          el('div', {
            class: 'toolbar env-toolbar',
            children: [
              addBtn,
              revealAllBtn,
              el('div', { class: 'spacer' }),
              reloadBtn,
              saveBtn,
              saveDeployBtn,
            ],
          }),
          rowsContainer,
          emptyHint,
        ],
      }),
    ],
  });

  const observer = new MutationObserver(() => {
    if (!view.isConnected) {
      window.removeEventListener('beforeunload', beforeUnload);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  void reload();
  return view;
}
